import PropTypes from 'prop-types';
import { forwardRef, useMemo } from 'react';
import { Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Provides a customized Avatar component that adapts its style based on the provided color and the device environment.
 * The Avatar's background and text colors are dynamically determined by the current theme and the specified color prop.
 *
 * @component
 * @param {Object} props - The properties passed to the Avatar component.
 * @param {React.ReactNode} props.children - The content of the avatar, typically an image or text.
 * @param {string} [props.color='default'] - Specifies the color variant for the avatar, which adjusts the background and text color. Accepts theme colors like 'primary', 'secondary', etc., or 'default' for the theme's default avatar color.
 * @param {Object} [props.sx] - The style object that can be used to override or extend the default styles of the avatar.
 * @param {Object} other - Additional props passed to the Avatar component.
 * @param {React.Ref} ref - Ref forwarded to the Avatar component for direct DOM access or instance reference.
 * @returns {JSX.Element} The Avatar component with applied customizations.
 *
 * @example
 * <MAvatar color="primary">A</MAvatar>
 *
 * @note The Avatar adapts its style based on the device environment. On mobile devices, it defaults to white background with primary text color.
 */
const MAvatar = forwardRef(({ children, color = 'default', sx, ...other }, ref) => {
  const {
    palette: { [color]: paletteColor },
    typography
  } = useTheme();

  const styleAvatar = useMemo(() => {
    // AppBar background color is primary color on mobile devices
    const backgroundColor = window.env.isMobileDevice ? 'white' : paletteColor.main;
    const textColor = window.env.isMobileDevice ? 'text.primary' : paletteColor.contrastText;

    return {
      fontWeight: typography.fontWeightMedium,
      color: textColor,
      backgroundColor
    };
  }, [color, window.env.isMobileDevice]);

  if (color === 'default') {
    return (
      <Avatar ref={ref} sx={sx} {...other}>
        {children}
      </Avatar>
    );
  }

  return (
    <Avatar ref={ref} sx={{ ...styleAvatar, ...sx }} {...other}>
      {children}
    </Avatar>
  );
});

MAvatar.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'info', 'success', 'warning', 'error'])
};

export default MAvatar;
